import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface QuotedMessage {
  id: string;
  sender_role: 'client' | 'coach';
  message_text: string;
  voice_url?: string | null;
}

interface ChatQuotedPreviewProps {
  message: QuotedMessage | null;
  /** How to label the original sender. */
  senderLabel: string;
  /** Compact = inside a bubble. Default = above the composer. */
  variant?: 'composer' | 'bubble';
  /** Render against a primary-coloured background (e.g. inside the user's own bubble). */
  onPrimary?: boolean;
  onCancel?: () => void;
  onClick?: () => void;
}

/**
 * WhatsApp-style quoted preview shown either above the composer when a reply is
 * being drafted, or inside a chat bubble to show which message this one replies to.
 */
export function ChatQuotedPreview({
  message,
  senderLabel,
  variant = 'bubble',
  onPrimary = false,
  onCancel,
  onClick,
}: ChatQuotedPreviewProps) {
  const isComposer = variant === 'composer';

  const preview = !message
    ? 'Original message unavailable'
    : message.voice_url
    ? '🎤 Voice message'
    : message.message_text;

  const containerClasses = cn(
    'relative flex items-start gap-2 rounded-md pl-2.5 pr-2 py-1.5 text-left',
    isComposer
      ? 'bg-muted/60 border border-border'
      : onPrimary
      ? 'bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors'
      : 'bg-foreground/5 hover:bg-foreground/10 transition-colors',
    onClick && 'cursor-pointer',
  );

  const accentClasses = cn(
    'absolute left-0 top-1 bottom-1 w-[3px] rounded-full',
    onPrimary && !isComposer ? 'bg-primary-foreground/70' : 'bg-primary',
  );

  const senderClasses = cn(
    'text-[11px] font-semibold leading-tight truncate',
    onPrimary && !isComposer ? 'text-primary-foreground' : 'text-primary',
  );

  const textClasses = cn(
    'text-xs leading-snug line-clamp-1 break-words',
    onPrimary && !isComposer ? 'text-primary-foreground/80' : 'text-muted-foreground',
    !message && 'italic',
  );

  const Tag: any = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={containerClasses}
    >
      <span className={accentClasses} aria-hidden="true" />
      <div className="min-w-0 flex-1 pl-1.5">
        <p className={senderClasses}>{senderLabel}</p>
        <p className={textClasses}>{preview}</p>
      </div>
      {onCancel && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0.5"
          aria-label="Cancel reply"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </Tag>
  );
}